import { Container } from "pixi.js";
import { PIXELS_PER_INCH } from "../shop-scale";
import {
  benchPlacementFor,
  benchTopSizeIn,
} from "../../game/bench-work/bench-layout";
import { isBenchType, Machine } from "../../game/Machine";
import { MaterialInstance } from "../../game/Materials";
import { Vector } from "../../game/Vectors";
import { materialHitExtentInches } from "../floor-picking";
import { createMaterialSprite } from "../material-sprites/MaterialSprite";

/**
 * A machine's staged stock — the old `MachineMaterials`: a bench's
 * pieces lying exactly where the bench layout says they lie, any other
 * machine's inputs and outputs fanned out on top of it, and the pieces
 * mid-operation gently rocking to show they're being worked on. The
 * rocking pauses while the machine is waiting for the player to come
 * back.
 *
 * The material drawings are the material sprites' (MaterialSprite.ts);
 * the placements and the rocking are all here.
 *
 * Under the bench dive's close-up the whole run's staged stock hides
 * instead of drawing (the leaned-over bench draws its own live stock) —
 * MachineView toggles it through the art's setStockHidden.
 *
 * A bench's pieces are also the cursor's: each holder is kept by its
 * material so the targeting rim can dress one piece (`pieceRoot`) and
 * the mouse can pick one out where it lies (`pieceUnderPoint`).
 */
export class StagedMaterials {
  /** Mounts on the machine's footprint center, like the old FootprintArt. */
  readonly root = new Container();
  private processing: Array<{ container: Container; baseAngle: number }> = [];
  private wobblePhase = 0;
  /** A bench's pieces by material, for the rim and the cursor. */
  private benchPieces: Array<{
    material: MaterialInstance;
    holder: Container;
  }> = [];

  rebuild(machine: Machine): void {
    this.root.removeChildren().forEach((child) => child.destroy());
    this.processing = [];
    this.benchPieces = [];

    if (isBenchType(machine.type)) {
      this.buildBenchTop(machine);
    } else {
      machine.inputMaterials.forEach((material, index) => {
        const holder = new Container();
        holder.angle = index * 10;
        const sprite = createMaterialSprite(material);
        if (sprite) holder.addChild(sprite);
        this.root.addChild(holder);
      });
    }

    machine.processingMaterials.forEach((material, index) => {
      const holder = new Container();
      const baseAngle = index * 10;
      holder.angle = baseAngle;
      const sprite = createMaterialSprite(material);
      if (sprite) holder.addChild(sprite);
      this.root.addChild(holder);
      this.processing.push({ container: holder, baseAngle });
    });

    // Bench outputs lie on the bench top through their placements; only
    // other machines fan theirs out
    if (!isBenchType(machine.type)) {
      machine.outputMaterials.forEach((material, index) => {
        const holder = new Container();
        holder.angle = index * 10 + 5;
        const sprite = createMaterialSprite(material);
        if (sprite) holder.addChild(sprite);
        this.root.addChild(holder);
      });
    }
  }

  /**
   * A bench's staged stock, lying exactly where the bench layout says it
   * lies (bench-work/bench-layout.ts) — the same arrangement the bench
   * view shows, at shop-floor zoom. Positions are bench-top inches
   * converted to footprint-center-relative pixels.
   */
  private buildBenchTop(machine: Machine): void {
    const { widthIn, heightIn } = benchTopSizeIn(machine.type);
    for (const material of [
      ...machine.inputMaterials,
      ...machine.outputMaterials,
    ]) {
      const placement = benchPlacementFor(machine, material);
      const holder = new Container();
      holder.position.set(
        (placement.xIn - widthIn / 2) * PIXELS_PER_INCH,
        (placement.yIn - heightIn / 2) * PIXELS_PER_INCH,
      );
      holder.angle = placement.angleDeg;
      holder.scale.set(placement.flipped ? -1 : 1, 1);
      const sprite = createMaterialSprite(material, {
        onEdge: placement.onEdge,
        flipped: placement.flipped,
      });
      if (sprite) holder.addChild(sprite);
      this.root.addChild(holder);
      this.benchPieces.push({ material, holder });
    }
  }

  /** The drawable of one piece lying on the bench top, for the rim. */
  pieceRoot(material: MaterialInstance): Container | null {
    return (
      this.benchPieces.find((piece) => piece.material.id === material.id)
        ?.holder ?? null
    );
  }

  /**
   * The bench-top piece under a point given in this root's own frame
   * (pixels from the footprint center, before the machine's rotation —
   * the caller carries the cursor into it). Tested against the rectangle
   * the piece's drawing covers, the way the floor's pieces are
   * (floor-picking.ts); the last one drawn is on top, so it answers
   * first.
   */
  pieceUnderPoint(point: Vector): MaterialInstance | null {
    for (let i = this.benchPieces.length - 1; i >= 0; i--) {
      const { material, holder } = this.benchPieces[i];
      const { across, along } = materialHitExtentInches(material);
      const halfX = (across * PIXELS_PER_INCH) / 2;
      const halfY = (along * PIXELS_PER_INCH) / 2;
      const cos = Math.cos(holder.rotation);
      const sin = Math.sin(holder.rotation);
      const wx = point[0] - holder.x;
      const wy = point[1] - holder.y;
      const lx = wx * cos + wy * sin;
      const ly = -wx * sin + wy * cos;
      if (Math.abs(lx) <= halfX && Math.abs(ly) <= halfY) return material;
    }
    return null;
  }

  /** Rock the pieces being worked on; frozen while `working` is false. */
  animate(dt: number, working: boolean): void {
    if (this.processing.length === 0) return;
    if (working) {
      this.wobblePhase = (this.wobblePhase + dt) % 0.8;
    }
    // The old sprite sprang between -2° and 2° every 400ms; a sine at the
    // same period and amplitude reads the same.
    const wobble = 2 * Math.sin((this.wobblePhase / 0.8) * Math.PI * 2);
    for (const { container, baseAngle } of this.processing) {
      container.angle = baseAngle + wobble;
    }
  }
}
